import { Logger } from '@mopla/logger';

/**
 * Represents a bottom-sheet snap point that can be specified in px or vh.
 *
 * Factory methods (preferred):
 *   SnapPoint.vh(0.65)  — 65 % of viewport height
 *   SnapPoint.px(300)   — 300 px (converted to vh fraction at read time)
 *
 * Instance setters (chainable, useful when the source unit is dynamic):
 *   new SnapPoint().setPx(calculatedValue)
 *   new SnapPoint().setVh(calculatedValue)
 */
export class SnapPoint {
	private _px?: number;
	private _vh?: number;

	/** Set value in pixels. Converted to a vh fraction on every read using the current window.innerHeight. */
	setPx(px: number): this {
		this._px = px;
		this._vh = undefined;
		return this;
	}

	/** Set value as a vh fraction (0–1), e.g. 0.65 means 65vh. */
	setVh(vh: number): this {
		this._vh = vh;
		this._px = undefined;
		return this;
	}

	/** Create a SnapPoint from pixels. */
	static px(px: number): SnapPoint {
		return new SnapPoint().setPx(px);
	}

	/** Create a SnapPoint from a vh fraction (0–1). */
	static vh(vh: number): SnapPoint {
		return new SnapPoint().setVh(vh);
	}

	/** Parse a vh CSS string (e.g. "65vh") into a SnapPoint. */
	static fromVhString(vhString: string): SnapPoint {
		const match = vhString.match(/^([\d.]+)vh$/);

		if (!match) {
			SnapPoint._logger.error('SnapPoint: cannot parse ', { vhString });

			throw new Error(`SnapPoint: cannot parse "${vhString}" as a vh value`);
		}

		return new SnapPoint().setVh(parseFloat(match[1]) / 100);
	}

	/** Get value as a vh fraction (0–1). Px-based points are converted using the current window.innerHeight. */
	toFraction(): number {
		if (this._vh !== undefined) {
			return this._vh;
		}

		if (this._px !== undefined) {
			return this._px / window.innerHeight;
		}

		const error = new Error(
			'SnapPoint: no value set — call setPx() or setVh() first'
		);

		SnapPoint._logger.error(error.message);

		throw error;
	}

	/** Get value as a vh CSS string, e.g. "65vh". */
	toVhString(): string {
		return `${this.toFraction() * 100}vh`;
	}

	static get _logger(): Logger {
		return Logger.getInstance('MplSnappingBottomSheet');
	}
}
